import React, { useState, useRef } from 'react';
import ReactPlayer from 'react-player';
import Image from '../../../components/AppImage';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { useAuth } from '../../../contexts/AuthContext';
import { postService } from '../../../lib/postService';
import ThreeModelViewer from './ThreeModelViewer';
import { useNavigate } from 'react-router-dom';
const getYoutubeId = (url) => {
  const match = url?.match(/(?:youtu\.be\/|youtube\.com\/(?:embed\/|v\/|watch\?v=|watch\?.+&v=))([^&?]+)/);
  return match ? match[1] : '';
};

const PostCard = ({ post }) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [isLiked, setIsLiked] = useState(post?.isLiked || (user && post?.likes?.some && post.likes.some(like => like.user_id === user.id)) || false);
  const [likeCount, setLikeCount] = useState(typeof post?.likes === 'number' ? post.likes : (post?.likes?.length || 0));
  const [showComments, setShowComments] = useState(false);
  const [newComment, setNewComment] = useState('');
  const [comments, setComments] = useState(post?.comments || []);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [isBookmarked, setIsBookmarked] = useState(false);
  const [showLikeAnimation, setShowLikeAnimation] = useState(false);
  
  const imageRef = useRef(null);

  // Instagram-style double tap to like
  const handleDoubleClick = async () => {
    if (!isLiked && user) {
      setIsLiked(true);
      setLikeCount(prev => prev + 1);
      setShowLikeAnimation(true);
      setTimeout(() => setShowLikeAnimation(false), 1000);
      try {
        await postService.toggleLike(post.id, user.id);
      } catch (error) {
        console.error("Error liking post:", error);
      }
    }
  };

  const handleLike = async () => {
    if (!user) return;
    const oldIsLiked = isLiked;
    setIsLiked(!isLiked);
    setLikeCount((prev) => isLiked ? prev - 1 : prev + 1);
    try {
      await postService.toggleLike(post.id, user.id);
    } catch (error) {
      setIsLiked(oldIsLiked);
      setLikeCount((prev) => oldIsLiked ? prev - 1 : prev + 1);
      console.error("Error toggling like:", error);
    }
  };

  const handleBookmark = () => {
    setIsBookmarked(!isBookmarked);
  };

  const handleComment = async () => {
    if (newComment?.trim() && user) {
      try {
        const commentData = await postService.addComment(post.id, user.id, newComment);
        setComments((prev) => [...prev, {
          ...commentData,
          author: user.user_metadata?.full_name || user.email,
          avatar: user.user_metadata?.avatar_url || "https://images.unsplash.com/photo-1662104935786-d2cb6ce587d8"
        }]);
        setNewComment('');
      } catch (error) {
        console.error("Error adding comment:", error);
      }
    }
  };

  const handleCommentLike = (commentId) => {
    setComments(prev => prev?.map(comment => 
      comment?.id === commentId 
        ? { 
            ...comment, 
            isLiked: !comment?.isLiked,
            likes: comment?.isLiked ? comment?.likes - 1 : comment?.likes + 1 
          }
        : comment
    ));
  };

  const nextImage = () => {
    if (post?.images?.length > 1) {
      setCurrentImageIndex((prev) => (prev + 1) % post?.images?.length);
    }
  };

  const prevImage = () => {
    if (post?.images?.length > 1) {
      setCurrentImageIndex((prev) => (prev - 1 + post?.images?.length) % post?.images?.length);
    }
  };

  const formatTimeAgo = (timestamp) => {
    const now = new Date();
    const diff = now - new Date(timestamp);
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);
    const weeks = Math.floor(diff / 604800000);

    if (weeks > 0) return `${weeks}w`;
    if (days > 0) return `${days}d`;
    if (hours > 0) return `${hours}h`;
    if (minutes > 0) return `${minutes}m`;
    return 'now';
  };

  return (
    <div className="glass-panel rounded-[32px] overflow-hidden mb-8 animate-fade-in group hover:border-white/30 transition-all duration-700">
      {/* Post header - Instagram style */}
      <div className="px-4 py-3 flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <div 
            className="w-8 h-8 rounded-full overflow-hidden ring-2 ring-primary/20 cursor-pointer"
            onClick={() => navigate(`/user-profile?id=${post?.author?.id || post?.user_id}`)}
          >
            <Image
              src={post?.author?.avatar || post?.author?.avatar_url || 'https://images.unsplash.com/photo-1662104935786-d2cb6ce587d8'}
              alt={post?.author?.avatarAlt || 'Author'}
              className="w-full h-full object-cover" />
          </div>
          <div>
            <p className="font-label-caps text-[10px] opacity-60 tracking-widest uppercase">
              {formatTimeAgo(post?.timestamp || post?.created_at)}
            </p>
            <h3 
              className="font-headline-sm text-headline-sm text-primary hover:text-primary/80 cursor-pointer transition-colors duration-150"
              onClick={() => navigate(`/user-profile?id=${post?.author?.id || post?.user_id}`)}
            >
              {post?.author?.name || post?.author?.full_name || 'Anonymous'}
            </h3>
          </div>
        </div>
        
        <Button variant="ghost" size="icon" className="text-muted-foreground hover:text-foreground">
          <Icon name="MoreHorizontal" size={18} />
        </Button>
      </div>

      {/* Post media */}
      {(post?.images?.length > 0 || post?.image_url || post?.video_url || post?.model_url || post?.media_type === 'model' || post?.media_type === 'video') && (
        <div className={`relative w-full h-[500px] md:h-[600px] bg-black overflow-hidden`}>
          
          {/* 3D Model */}
          {(post?.media_type === 'model' || post?.model_url) && (
            <ThreeModelViewer modelUrl={post?.model_url || post?.image_url} />
          )}
          {/* VIDEO / YOUTUBE — React Player */}
          {!post?.model_url && post?.media_type !== 'model' && (post?.video_url || post?.media_type === 'video') && (
            post?.video_url?.includes('youtube.com') || post?.video_url?.includes('youtu.be') || post?.video_url?.includes('dailymotion.com') ? (
              <div className="absolute inset-0 w-full h-full bg-black">
                <ReactPlayer 
                  url={post?.video_url} 
                  width="100%" 
                  height="100%" 
                  controls 
                  playing={false}
                  config={{ 
                    youtube: { playerVars: { origin: 'https://freezone-nine.vercel.app' } },
                    dailymotion: { params: { 'ui-theme': 'dark' } } 
                  }}
                />
              </div>
            ) : (
              <video
                key={post?.video_url}
                src={post?.video_url}
                className="w-full h-full relative z-10"
                style={{ objectFit: 'contain', background: '#000', display: 'block' }}
                controls
                playsInline
                preload="metadata"
              />
            )
          )}

          {/* IMAGE with double-tap to like */}
          {!post?.model_url && post?.media_type !== 'model' && !post?.video_url && post?.media_type !== 'video' && (
            <div
              ref={imageRef}
              className="relative w-full h-full cursor-pointer"
              onDoubleClick={handleDoubleClick}
            >
              <Image
                src={post?.image_url || post?.images?.[currentImageIndex]?.url}
                alt={post?.images?.[currentImageIndex]?.alt || 'Post image'}
                className="w-full h-full object-cover select-none"
                draggable={false}
              />

              {/* Double-tap like animation */}
              {showLikeAnimation && (
                <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                  <div className="animate-ping">
                    <Icon name="Heart" size={80} color="red" fill="red" />
                  </div>
                </div>
              )}

              {/* Carousel navigation */}
              {post?.images?.length > 1 && (
                <>
                  <button
                    onClick={prevImage}
                    className="absolute left-2 top-1/2 transform -translate-y-1/2 w-8 h-8 bg-black/50 rounded-full flex items-center justify-center text-white hover:bg-black/70 transition-all"
                  >
                    <Icon name="ChevronLeft" size={16} />
                  </button>
                  <button
                    onClick={nextImage}
                    className="absolute right-2 top-1/2 transform -translate-y-1/2 w-8 h-8 bg-black/50 rounded-full flex items-center justify-center text-white hover:bg-black/70 transition-all"
                  >
                    <Icon name="ChevronRight" size={16} />
                  </button>
                  <div className="absolute bottom-3 left-1/2 transform -translate-x-1/2 flex space-x-1">
                    {post?.images?.map((_, index) => (
                      <div
                        key={index}
                        className={`w-2 h-2 rounded-full transition-all ${index === currentImageIndex ? 'bg-white' : 'bg-white/50'}`}
                      />
                    ))}
                  </div>
                </>
              )}
            </div>
          )}

        </div>
      )}

      {/* Instagram-style action buttons */}
      <div className="px-4 py-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <button
              onClick={handleLike}
              className={`transition-all duration-200 hover:scale-110 ${
                isLiked ? 'text-red-500' : 'text-foreground hover:text-muted-foreground'
              }`}
            >
              <Icon name="Heart" size={24} fill={isLiked ? 'currentColor' : 'none'} />
            </button>
            
            <button 
              onClick={() => setShowComments(!showComments)}
              className="text-foreground hover:text-muted-foreground transition-colors hover:scale-110"
            >
              <Icon name="MessageCircle" size={24} />
            </button>
            
            <button className="text-foreground hover:text-muted-foreground transition-colors hover:scale-110">
              <Icon name="Send" size={24} />
            </button>
          </div>
          
          <button
            onClick={handleBookmark}
            className={`transition-all duration-200 hover:scale-110 ${
              isBookmarked ? 'text-foreground' : 'text-muted-foreground hover:text-foreground'
            }`}
          >
            <Icon name="Bookmark" size={24} fill={isBookmarked ? 'currentColor' : 'none'} />
          </button>
        </div>

        {/* Like count */}
        {likeCount > 0 && (
          <div className="mt-2">
            <p className="font-semibold text-sm text-foreground">
              {likeCount?.toLocaleString()} {likeCount === 1 ? 'like' : 'likes'}
            </p>
          </div>
        )}

        {/* Post content */}
        {post?.content && (
          <div className="mt-4 mb-2">
            <p className="font-body-md text-primary/80 opacity-80 leading-relaxed whitespace-pre-wrap">
              {post?.content}
            </p>
          </div>
        )}

        {/* View all comments */}
        {comments?.length > 0 && !showComments && (
          <button
            onClick={() => setShowComments(true)}
            className="mt-2 text-sm text-muted-foreground hover:text-foreground transition-colors"
          >
            View all {comments?.length} comments
          </button>
        )}

        {/* Comments preview (show last 2) */}
        {comments?.length > 0 && !showComments && (
          <div className="mt-2 space-y-1">
            {comments?.slice(-2)?.map((comment) => (
              <p key={comment?.id} className="text-sm text-primary/80 font-body-md">
                <span className="font-label-caps text-[10px] tracking-wider uppercase opacity-70 mr-2">{comment?.author?.name || comment?.author?.full_name || comment?.author}</span>
                <span>{comment?.content}</span>
              </p>
            ))}
          </div>
        )}

        {/* Add comment */}
        <div className="mt-3 flex items-center space-x-3">
          <div className="w-6 h-6 rounded-full overflow-hidden">
            <Image
              src="https://images.unsplash.com/photo-1662104935786-d2cb6ce587d8"
              alt="Your profile photo"
              className="w-full h-full object-cover"
            />
          </div>
          <input
            type="text"
            value={newComment}
            onChange={(e) => setNewComment(e?.target?.value)}
            placeholder="Add a comment..."
            className="flex-1 bg-transparent text-sm focus:outline-none placeholder-primary/30 text-primary border-none focus:ring-0"
            onKeyPress={(e) => e?.key === 'Enter' && handleComment()}
          />
          {newComment?.trim() && (
            <button
              onClick={handleComment}
              className="text-sm font-semibold text-primary hover:text-primary/80 transition-colors"
            >
              Post
            </button>
          )}
        </div>
      </div>

      {/* Expanded comments section */}
      {showComments && (
        <div className="border-t border-border max-h-96 overflow-y-auto">
          {comments?.map((comment) => (
            <div key={comment?.id} className="px-4 py-3 flex items-start space-x-3 hover:bg-muted/30 transition-colors">
              <div className="w-6 h-6 rounded-full overflow-hidden">
                <Image
                  src={comment?.avatar || comment?.author?.avatar_url || 'https://images.unsplash.com/photo-1662104935786-d2cb6ce587d8'}
                  alt={comment?.avatarAlt || 'Comment author'}
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="flex-1">
                <p className="text-sm text-foreground">
                  <span className="font-semibold">{comment?.author?.name || comment?.author?.full_name || comment?.author}</span>{' '}
                  <span>{comment?.content}</span>
                </p>
                <div className="flex items-center space-x-4 mt-1 text-xs text-muted-foreground">
                  <span>{formatTimeAgo(comment?.timestamp || comment?.created_at)}</span>
                  {comment?.likes > 0 && (
                    <span>{comment?.likes} {comment?.likes === 1 ? 'like' : 'likes'}</span>
                  )}
                  <button className="hover:text-foreground transition-colors">
                    Reply
                  </button>
                </div>
              </div>
              <button
                onClick={() => handleCommentLike(comment?.id)}
                className={`transition-colors ${
                  comment?.isLiked ? 'text-red-500' : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                <Icon name="Heart" size={12} fill={comment?.isLiked ? 'currentColor' : 'none'} />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default PostCard;